/**
 * HTTP Service
 *
 * Provides a pre-configured fetch-backed client for making HTTP requests.
 * This service is used across the application for API calls.
 *
 * @example
 * ```typescript
 * import { http } from '@/services/http'
 *
 * // GET request
 * const response = await http.get('/api/endpoint')
 *
 * // POST request
 * const response = await http.post('/api/endpoint', { data: 'value' })
 *
 * // With custom config
 * const response = await http.get('/api/endpoint', { timeout: 5000 })
 * ```
 */

import {classifyError, getTargetSystem} from '@/services/http/errors'
import type {HttpTelemetry} from '@/services/http/errors'
import {redactHeaders, redactUrl} from '@/services/http/redact'

type HttpMethod =
	| 'GET'
	| 'POST'
	| 'PUT'
	| 'PATCH'
	| 'DELETE'
	| 'HEAD'
	| 'OPTIONS'

interface HttpRequestConfig<D = unknown> {
	url?: string
	method?: HttpMethod | Lowercase<HttpMethod>
	params?: Record<string, unknown>
	headers?: HeadersInit
	data?: D
	timeout?: number
	validateStatus?: (status: number) => boolean
	responseType?: 'json' | 'text' | 'blob' | 'arraybuffer'
	signal?: AbortSignal
}

interface HttpResponse<T = unknown> {
	data: T
	status: number
	statusText: string
	headers: Record<string, string>
	config: HttpRequestConfig
	request: Request | {url: string; method: HttpMethod}
}

interface HttpError<T = unknown> extends Error {
	isAxiosError: true
	code?: string
	config: HttpRequestConfig
	request: Request | {url: string; method: HttpMethod}
	response?: HttpResponse<T>
	httpTelemetry?: HttpTelemetry
	category?: ReturnType<typeof classifyError>
}

/**
 * Runtime type guard for errors thrown by this HTTP service.
 *
 * Prefer this over `error instanceof AxiosError` (from the `axios` package) —
 * this service no longer throws real `AxiosError` instances. It remains
 * compatible with `axios.isAxiosError(error)` duck-typing checks used
 * elsewhere in the codebase, since both look for `isAxiosError === true`.
 */
export function isHttpError<T = unknown>(
	error: unknown
): error is HttpError<T> {
	return (
		typeof error === 'object' &&
		error !== null &&
		'isAxiosError' in error &&
		(error as {isAxiosError?: unknown}).isAxiosError === true
	)
}

interface HttpClient {
	<T = unknown, R = HttpResponse<T>, D = unknown>(
		config: HttpRequestConfig<D>
	): Promise<R>
	<T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	): Promise<R>
	request: <T = unknown, R = HttpResponse<T>, D = unknown>(
		config: HttpRequestConfig<D>
	) => Promise<R>
	get: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	delete: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	head: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	options: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	post: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	put: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	) => Promise<R>
	patch: <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	) => Promise<R>
}

const DEFAULT_TIMEOUT = 30_000

function isPlainObject(value: unknown): value is Record<string, unknown> {
	if (value === null || typeof value !== 'object') {
		return false
	}

	const prototype: unknown = Object.getPrototypeOf(value)
	return prototype === Object.prototype || prototype === null
}

function buildRequestUrl(
	url: string,
	params?: Record<string, unknown>
): string {
	if (!params || Object.keys(params).length === 0) {
		return url
	}

	const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(url)
	const baseUrl =
		typeof window === 'undefined'
			? 'http://localhost'
			: globalThis.location.origin
	const parsedUrl = new URL(url, baseUrl)

	for (const [key, value] of Object.entries(params)) {
		if (value === null || value === undefined) {
			continue
		}

		if (Array.isArray(value)) {
			for (const entry of value) {
				parsedUrl.searchParams.append(key, String(entry))
			}
			continue
		}

		parsedUrl.searchParams.append(key, String(value))
	}

	return isAbsoluteUrl
		? parsedUrl.toString()
		: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
}

function headersToObject(headers: Headers): Record<string, string> {
	const output: Record<string, string> = {}
	headers.forEach((value, key) => {
		output[key] = value
	})
	return output
}

async function parseResponseData<T>(
	response: Response,
	responseType?: HttpRequestConfig['responseType']
): Promise<T> {
	if (response.status === 204 || response.status === 205) {
		return '' as T
	}

	if (responseType === 'arraybuffer') {
		return (await response.arrayBuffer()) as T
	}

	if (responseType === 'blob') {
		return (await response.blob()) as T
	}

	if (responseType === 'text') {
		return (await response.text()) as T
	}

	const contentType =
		response.headers.get('content-type')?.toLowerCase() ?? ''
	if (contentType.includes('application/json')) {
		return (await response.json()) as T
	}

	const text = await response.text()
	if (!text) {
		return '' as T
	}

	try {
		return JSON.parse(text) as T
	} catch {
		return text as T
	}
}

function buildHttpError<T>(params: {
	message: string
	config: HttpRequestConfig
	request: Request | {url: string; method: HttpMethod}
	response?: HttpResponse<T>
	code?: string
	category?: ReturnType<typeof classifyError>
}): HttpError<T> {
	const error = new Error(params.message) as HttpError<T>
	error.name = 'HttpError'
	error.isAxiosError = true
	error.code = params.code
	error.config = params.config
	error.request = params.request
	error.response = params.response
	error.category = params.category
	return error
}

function buildRequestDescriptor(
	url: string,
	method: HttpMethod
): Request | {url: string; method: HttpMethod} {
	try {
		return new Request(url, {method})
	} catch {
		return {url, method}
	}
}

function mergeAbortSignals(
	timeoutMs: number,
	externalSignal?: AbortSignal
): {
	signal: AbortSignal
	cleanup: () => void
	didTimeout: () => boolean
} {
	const controller = new AbortController()
	let timedOut = false

	const timeoutId = globalThis.setTimeout(() => {
		timedOut = true
		controller.abort()
	}, timeoutMs)

	const abortFromExternalSignal = (): void => {
		controller.abort()
	}

	if (externalSignal) {
		if (externalSignal.aborted) {
			controller.abort()
		} else {
			externalSignal.addEventListener('abort', abortFromExternalSignal)
		}
	}

	const cleanup = (): void => {
		clearTimeout(timeoutId)
		if (externalSignal) {
			externalSignal.removeEventListener('abort', abortFromExternalSignal)
		}
	}

	return {
		signal: controller.signal,
		cleanup,
		didTimeout: () => timedOut,
	}
}

/**
 * Emits the standard `OUTBOUND_HTTP_REQUEST` telemetry event. Only wired up
 * server-side, where the App Insights server SDK and secret-bearing
 * headers/URLs actually live.
 */
function logRequestTelemetry(params: {
	url: string
	method: HttpMethod
	durationMs: number
	status?: number
	failureCategory?: ReturnType<typeof classifyError>
	requestHeaders?: HeadersInit
}): void {
	if (typeof window !== 'undefined') {
		return
	}

	try {
		// eslint-disable-next-line @typescript-eslint/no-var-requires -- lazy require avoids client-bundle inclusion
		const {logEvent} = require('@/services/logger/server') as {
			logEvent: (event: {name: string; properties: object}) => void
		}

		const outcome = params.failureCategory ? 'failure' : 'success'

		logEvent({
			name: 'OUTBOUND_HTTP_REQUEST',
			properties: {
				targetSystem: getTargetSystem(params.url),
				method: params.method,
				status: params.status,
				durationMs: params.durationMs,
				url: redactUrl(params.url),
				outcome,
				...(params.failureCategory
					? {
							failureCategory: params.failureCategory,
							requestHeaders: redactHeaders(
								params.requestHeaders
									? headersToObject(
											new Headers(params.requestHeaders)
										)
									: undefined
							),
						}
					: {}),
			},
		})
	} catch {
		// Swallow telemetry failures — see comment above. There is
		// intentionally nowhere else for this to go: if the logger itself is
		// broken, trying to log that failure would risk the same issue.
	}
}

async function request<T = unknown, R = HttpResponse<T>, D = unknown>(
	url: string,
	config: HttpRequestConfig<D>
): Promise<R> {
	const method = (config.method ?? 'GET').toUpperCase() as HttpMethod
	const requestUrl = buildRequestUrl(url, config.params)
	const requestDescriptor = buildRequestDescriptor(requestUrl, method)
	const timeout = config.timeout ?? DEFAULT_TIMEOUT
	const {signal, cleanup, didTimeout} = mergeAbortSignals(
		timeout,
		config.signal
	)
	const startTime = Date.now()

	const headers = new Headers({'Content-Type': 'application/json'})
	if (config.headers) {
		new Headers(config.headers).forEach((value, key) => {
			headers.set(key, value)
		})
	}

	const hasRequestBody =
		method !== 'GET' && method !== 'HEAD' && config.data !== undefined

	let body: BodyInit | undefined
	if (hasRequestBody) {
		if (isPlainObject(config.data) || Array.isArray(config.data)) {
			body = JSON.stringify(config.data)
		} else {
			body = config.data as BodyInit
		}
	}

	const emitFailureTelemetry = (
		httpError: HttpError,
		status?: number
	): void => {
		const durationMs = Date.now() - startTime
		const category = classifyError({
			code: httpError.code,
			message: httpError.message,
			response: status !== undefined ? {status} : undefined,
			category: httpError.category,
		})

		httpError.httpTelemetry = {
			category,
			targetSystem: getTargetSystem(requestUrl),
			method,
			status,
			durationMs,
			redactedUrl: redactUrl(requestUrl),
		}

		logRequestTelemetry({
			url: requestUrl,
			method,
			durationMs,
			status,
			failureCategory: category,
			requestHeaders: headers,
		})
	}

	try {
		// eslint-disable-next-line no-restricted-globals -- this is the approved, single implementation of the shared HTTP client (see src/services/http/README.md)
		const response = await fetch(requestUrl, {
			method,
			headers,
			signal,
			body,
		})

		const parsedData = await parseResponseData<T>(
			response,
			config.responseType
		).catch((parseError: unknown) => {
			const message =
				parseError instanceof Error
					? parseError.message
					: 'Failed to parse response body'
			const httpError = buildHttpError({
				message: `Response validation failed: ${message}`,
				config,
				request: requestDescriptor,
				response: {
					data: undefined as T,
					status: response.status,
					statusText: response.statusText,
					headers: headersToObject(response.headers),
					config,
					request: requestDescriptor,
				},
				// Only classify parse failures as `validation` when the
				// response itself was successful (2xx) -- the documented
				// contract reserves `validation` for that case. A non-2xx
				// response with an unparsable body is still an `http_error`.
				category: response.ok ? 'validation' : 'http_error',
			})
			emitFailureTelemetry(httpError, response.status)
			throw httpError
		})
		const parsedResponse: HttpResponse<T> = {
			data: parsedData,
			status: response.status,
			statusText: response.statusText,
			headers: headersToObject(response.headers),
			config,
			request: requestDescriptor,
		}

		const isValid = config.validateStatus
			? config.validateStatus(response.status)
			: response.ok

		if (!isValid) {
			const httpError = buildHttpError({
				message: `Request failed with status code ${response.status}`,
				config,
				request: requestDescriptor,
				response: parsedResponse,
				category: response.ok ? 'validation' : 'http_error',
			})
			emitFailureTelemetry(httpError, response.status)
			throw httpError
		}

		if (!response.ok) {
			logRequestTelemetry({
				url: requestUrl,
				method,
				durationMs: Date.now() - startTime,
				status: response.status,
				failureCategory: 'http_error',
				requestHeaders: headers,
			})
		} else {
			logRequestTelemetry({
				url: requestUrl,
				method,
				durationMs: Date.now() - startTime,
				status: response.status,
			})
		}

		return parsedResponse as R
	} catch (error) {
		if (isHttpError(error)) {
			throw error
		}

		if (error instanceof DOMException && error.name === 'AbortError') {
			if (didTimeout()) {
				const httpError = buildHttpError({
					message: `timeout of ${timeout}ms exceeded`,
					config,
					request: requestDescriptor,
					code: 'ECONNABORTED',
				})
				emitFailureTelemetry(httpError)
				throw httpError
			}

			const httpError = buildHttpError({
				message: 'canceled',
				config,
				request: requestDescriptor,
				code: 'ERR_CANCELED',
			})
			emitFailureTelemetry(httpError)
			throw httpError
		}

		const message = error instanceof Error ? error.message : 'Network Error'
		const httpError = buildHttpError({
			message,
			config,
			request: requestDescriptor,
		})
		emitFailureTelemetry(httpError)
		throw httpError
	} finally {
		cleanup()
	}
}

/**
 * Pre-configured fetch-backed client with:
 * - 30 second timeout
 * - JSON content-type header by default
 * - Axios-compatible response and error shape for existing callsites
 * - Standard outbound HTTP telemetry (server-side only)
 */
const createHttpClient = (): HttpClient => {
	const httpFn = (<T = unknown, R = HttpResponse<T>, D = unknown>(
		urlOrConfig: string | HttpRequestConfig<D>,
		config?: HttpRequestConfig<D>
	): Promise<R> => {
		if (typeof urlOrConfig === 'string') {
			return request<T, R, D>(urlOrConfig, config ?? {})
		}

		if (!urlOrConfig.url) {
			throw new Error('Request config must include a url')
		}

		return request<T, R, D>(urlOrConfig.url, urlOrConfig)
	}) as HttpClient

	httpFn.request = <T = unknown, R = HttpResponse<T>, D = unknown>(
		config: HttpRequestConfig<D>
	): Promise<R> => {
		if (!config.url) {
			throw new Error('Request config must include a url')
		}

		return request<T, R, D>(config.url, config)
	}

	httpFn.get = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'GET'})

	httpFn.delete = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'DELETE'})

	httpFn.head = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'HEAD'})

	httpFn.options = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'OPTIONS'})

	httpFn.post = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'POST', data})

	httpFn.put = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'PUT', data})

	httpFn.patch = <T = unknown, R = HttpResponse<T>, D = unknown>(
		url: string,
		data?: D,
		config?: HttpRequestConfig<D>
	): Promise<R> => request<T, R, D>(url, {...config, method: 'PATCH', data})

	return httpFn
}

export const http = createHttpClient()
